<template>
    <mode-switcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('penalty_creating')"
        :edit-mode-text="$t('penalty_editing')"
        @reset="penaltyEditModalSelected = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :items="penaltiesFiltered"
                :headers="headers"
                :loading="isPenaltiesLoading"
                class="elevation-1 table-server"
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
            >
                <template v-slot:item.name="{item}">
                    <span class="text-link font-weight-medium text-decoration-underline" @click="showEditModal(item)">
                        {{ item.name }}
                    </span>
                </template>
                <template v-slot:item.penaltyConditions="{item}">
                    <div class="d-flex flex-column">
                        <span v-for="(condition, index) in item.penaltyConditions" :key="index">
                            {{ getPenaltyConditionLabel(condition, item.tariffLinks) }}
                        </span>
                    </div>
                </template>
                <template v-slot:item.active="{item}">
                    <span>
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </span>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <product-prices-penalties-modal
                :penalty="penaltyEditModalSelected"
                :contract="contract"
                :selected-locale="selectedLocale"
                :condition-price-type-items="conditionPriceTypeItems"
                @on-close="onCloseEditModal"
                @on-save="onPenaltySave"
            />
        </template>
    </mode-switcher>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Watch} from 'vue-facing-decorator'
    import ProductPricesPenaltiesModal from '~/components/supplier/prices/modals/ProductPricesPenaltiesModal.vue'

    @Component({
        components: {ProductPricesPenaltiesModal},
    })
    export class ProductPricesPenalties extends Vue {
        @Prop() contract
        @Prop() selectedLocale
        @Prop() penalties
        @Prop() isPenaltiesLoading

        penaltyEditModalSelected = null
        activeFilter = true

        async mounted() {
            await supplierStore.loadTariffs({contractId: this.contract.id})
        }

        @Watch('activeFilter')
        onActiveFilterChange() {
            this.onPenaltiesFilterChange()
        }

        @Emit()
        async onPenaltySave() {}

        @Emit()
        onPenaltiesFilterChange() {}

        showEditModal(penalty) {
            this.penaltyEditModalSelected = penalty
            this.$refs.modeSwitcher.edit()
        }

        onCloseEditModal() {
            this.$refs.modeSwitcher.goToTable()
        }

        getPenaltyConditionLabel(condition, tariffLinks) {
            let currency = 'USD'
            if (this.tariffs?.length && tariffLinks?.length) {
                const linkedTariff = this.tariffs.find(({id}) => id === tariffLinks[0].tariffId)
                if (linkedTariff?.currencyCode) {
                    currency = linkedTariff.currencyCode
                }
            }
            const priceTypeLabel = this.conditionPriceTypeItems.find(el => el.value === condition.priceType)?.title

            return `${condition.unitOfMeasurement === 'PERCENT' ? condition.price : condition.price.toFixed(2)} ${
                condition.unitOfMeasurement === 'PERCENT'
                    ? '% ' + this.$t('penalty_cancel_description.from_the') + ` ${priceTypeLabel || ''}`
                    : currency || 'USD'
            }`
        }

        get conditionPriceTypeItems() {
            const whole = {title: this.$t('penalty_conditions_type.WHOLE_ORDER'), value: 'WHOLE_ORDER'}
            const nights = Array.from(Array(14).keys())
                .map(n => `NIGHT_${n + 1}`)
                .map((el, index) => ({
                    title: `${index + 1} ${this.$t('night', index + 1)}`,
                    value: el,
                }))

            return [whole, ...nights]
        }

        get headers() {
            return [
                {title: this.$t('name'), value: 'name', sortable: true},
                {title: this.$t('conditions'), value: 'penaltyConditions'},
                {title: this.$t('status'), value: 'active'},
            ]
        }

        get penaltiesFiltered() {
            return this.penalties
        }

        get tariffs() {
            return supplierStore.tariffsForPrice
        }
    }

    export default ProductPricesPenalties
</script>
